import { applyStepRole } from './steps.ts';
import { messagePreview } from './search.ts';
import type { PullResult, PushResult, SyncOp } from './sync.ts';
import { uuidv7 } from './sync.ts';
import type { BoardColumn, ChecklistItem, EntityMap, EntityName, FormResponse, Media, Message, ProjectView, Task, TaskUserState } from './types.ts';
import { answerMediaIds, type FormAnswers, type QuestionForm } from './forms.ts';
import { ENTITY_NAMES } from './types.ts';

/**
 * Offline-first Sync-Client für Web und Mobile.
 *
 * - Alle Daten liegen lokal (Speicher-Adapter je Plattform).
 * - Änderungen werden sofort lokal angewendet und in die Outbox gelegt.
 * - `flush()` lädt erst ausstehende Medien hoch, schickt dann die Outbox
 *   und holt anschließend Änderungen vom Server.
 */

export type EntityStore = { [K in EntityName]: Map<string, EntityMap[K]> };

export interface PendingOp {
  op: SyncOp;
  queuedAt: string;
}

export interface PendingUpload {
  mediaId: string;
  kind: Media['kind'];
  /** Plattformspezifisch: Blob (Web) oder Datei-URI (Mobile). */
  file: unknown;
  fileName: string | null;
  mime: string | null;
  url: string | null;
  durationSec: number | null;
  width: number | null;
  height: number | null;
  queuedAt: string;
}

export interface ClientStorage {
  load(): Promise<{
    rows: { entity: EntityName; data: any }[];
    outbox: PendingOp[];
    uploads: PendingUpload[];
    meta: Record<string, string>;
  }>;
  putRows(rows: { entity: EntityName; id: string; data: any | null }[]): Promise<void>;
  putOutbox(op: PendingOp): Promise<void>;
  removeOutbox(opIds: string[]): Promise<void>;
  putUpload(u: PendingUpload): Promise<void>;
  removeUpload(mediaId: string): Promise<void>;
  setMeta(key: string, value: string): Promise<void>;
}

export interface Transport {
  push(body: { deviceId: string; ops: SyncOp[] }): Promise<PushResult>;
  pull(cursor: number): Promise<PullResult>;
  upload(u: PendingUpload): Promise<void>;
}

export interface SyncStatus {
  online: boolean;
  syncing: boolean;
  pendingOps: number;
  pendingUploads: number;
  lastError: string | null;
  lastSyncAt: string | null;
}

export interface Identity {
  userId: string;
  userName: string;
}

type Listener = () => void;

const emptyStore = (): EntityStore =>
  Object.fromEntries(ENTITY_NAMES.map((n) => [n, new Map()])) as unknown as EntityStore;

export class SyncClient {
  readonly data: EntityStore = emptyStore();
  status: SyncStatus = { online: true, syncing: false, pendingOps: 0, pendingUploads: 0, lastError: null, lastSyncAt: null };
  /** Vom Server abgelehnte Operationen (zur Anzeige). */
  rejected: { opId: string; error: string }[] = [];
  /** Wird bei jeder Datenänderung erhöht; praktisch für React-Selektoren. */
  version = 0;

  /** Letzter Fehler je ausstehendem Upload (für „Nicht gesendet, erneut versuchen"). */
  private uploadErrors = new Map<string, string>();
  private outbox: PendingOp[] = [];
  private uploads: PendingUpload[] = [];
  private cursor = 0;
  private deviceId = '';
  private listeners = new Set<Listener>();
  private flushing: Promise<void> | null = null;
  private flushAgain = false;

  constructor(
    private storage: ClientStorage,
    private transport: Transport,
    private me: Identity,
  ) {}

  /** Angemeldete Person dieses Clients (bei Freigaben die Gast-Identität). */
  get identity(): Identity {
    return this.me;
  }

  async init(): Promise<void> {
    const { rows, outbox, uploads, meta } = await this.storage.load();
    for (const r of rows) (this.data[r.entity] as Map<string, any>).set(r.data.id, r.data);
    this.outbox = outbox.sort((a, b) => a.queuedAt.localeCompare(b.queuedAt));
    this.uploads = uploads.sort((a, b) => a.queuedAt.localeCompare(b.queuedAt));
    this.cursor = Number(meta.cursor ?? 0);
    this.deviceId = meta.deviceId ?? '';
    if (!this.deviceId) {
      this.deviceId = uuidv7();
      await this.storage.setMeta('deviceId', this.deviceId);
    }
    this.status.lastSyncAt = meta.lastSyncAt ?? null;
    this.emit();
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit() {
    this.version++;
    this.status = { ...this.status, pendingOps: this.outbox.length, pendingUploads: this.uploads.length };
    for (const fn of this.listeners) fn();
  }

  setOnline(online: boolean) {
    if (this.status.online === online) return;
    this.status = { ...this.status, online };
    this.emit();
    if (online) void this.flush();
  }

  /** Grund, warum ein Medium zuletzt nicht hochgeladen werden konnte, sonst null. */
  uploadError(mediaId: string): string | null {
    return this.uploadErrors.get(mediaId) ?? null;
  }

  /** Fehlgeschlagene Übertragungen erneut anstoßen (ein Medium oder alle). */
  retry(mediaId?: string): Promise<void> {
    if (mediaId) this.uploadErrors.delete(mediaId);
    else this.uploadErrors.clear();
    this.emit();
    return this.flush();
  }

  /** Lokale Datei eines noch nicht hochgeladenen Mediums (Blob im Web, URI in der App). */
  localFile(mediaId: string): unknown | null {
    return this.uploads.find((u) => u.mediaId === mediaId)?.file ?? null;
  }

  isPending(entity: EntityName, id: string): boolean {
    return this.outbox.some((p) => p.op.entity === entity && opKey(p.op, this.me.userId) === id) ||
      (entity === 'media' && this.uploads.some((u) => u.mediaId === id));
  }

  // ---------------------------------------------------------------------------
  // Lokale Änderungen

  private setLocal<K extends EntityName>(entity: K, row: EntityMap[K]) {
    (this.data[entity] as Map<string, EntityMap[K]>).set(row.id, row);
  }

  private async queue(op: SyncOp, touched: { entity: EntityName; id: string }[]) {
    const pending = { op, queuedAt: new Date().toISOString() };
    this.outbox.push(pending);
    await this.storage.putOutbox(pending);
    await this.persist(touched);
    this.emit();
    void this.flush();
  }

  private async persist(touched: { entity: EntityName; id: string }[]) {
    await this.storage.putRows(
      touched.map((t) => ({ entity: t.entity, id: t.id, data: (this.data[t.entity] as Map<string, any>).get(t.id) ?? null })),
    );
  }

  async saveBusiness(input: { id?: string; name: string; color?: string | null; archivedAt?: string | null }) {
    const ts = new Date().toISOString();
    const id = input.id ?? uuidv7();
    const prev = this.data.business.get(id);
    this.setLocal('business', {
      id,
      name: input.name,
      color: input.color ?? prev?.color ?? null,
      isDefault: prev?.isDefault ?? false,
      archivedAt: input.archivedAt ?? null,
      createdAt: prev?.createdAt ?? ts,
      updatedAt: ts,
    });
    await this.queue({ opId: uuidv7(), entity: 'business', op: 'upsert', data: { id, name: input.name, color: input.color ?? null, archivedAt: input.archivedAt ?? null } }, [{ entity: 'business', id }]);
    return id;
  }

  /**
   * Bild vormerken (z. B. Projektbild oder Logo). Liefert die Medien-ID, auf
   * die man sofort verweisen kann; hochgeladen wird beim nächsten Abgleich.
   */
  async queueImage(u: Omit<PendingUpload, 'mediaId' | 'queuedAt' | 'kind' | 'url' | 'durationSec'>): Promise<string> {
    const ts = new Date().toISOString();
    const mediaId = uuidv7();
    this.setLocal('media', {
      id: mediaId,
      kind: 'bild',
      mime: u.mime,
      fileName: u.fileName,
      size: null,
      durationSec: null,
      width: u.width,
      height: u.height,
      status: 'hochladen',
      url: null,
      linkTitle: null,
      linkSite: null,
      linkDescription: null,
      linkImage: false,
      summary: null,
      points: [],
      frames: [],
      transcript: null,
      imageText: null,
      aiError: null,
      aiAt: null,
      createdAt: ts,
      updatedAt: ts,
    });
    const pending: PendingUpload = { ...u, kind: 'bild', url: null, durationSec: null, mediaId, queuedAt: ts };
    this.uploads.push(pending);
    await this.storage.putUpload(pending);
    await this.persist([{ entity: 'media', id: mediaId }]);
    return mediaId;
  }

  async saveProject(input: { id?: string; businessId: string; name: string; color?: string | null; imageMediaId?: string | null; view?: ProjectView; archivedAt?: string | null }) {
    const ts = new Date().toISOString();
    const id = input.id ?? uuidv7();
    const prev = this.data.project.get(id);
    this.setLocal('project', {
      id,
      businessId: input.businessId,
      name: input.name,
      color: input.color ?? prev?.color ?? null,
      imageMediaId: input.imageMediaId !== undefined ? input.imageMediaId : (prev?.imageMediaId ?? null),
      isInternal: prev?.isInternal ?? false,
      view: input.view ?? prev?.view ?? 'liste',
      archivedAt: input.archivedAt ?? null,
      createdAt: prev?.createdAt ?? ts,
      updatedAt: ts,
    });
    await this.queue(
      {
        opId: uuidv7(),
        entity: 'project',
        op: 'upsert',
        data: {
          id,
          businessId: input.businessId,
          name: input.name,
          color: input.color ?? null,
          archivedAt: input.archivedAt ?? null,
          ...(input.view !== undefined && { view: input.view }),
          ...(input.imageMediaId !== undefined && { imageMediaId: input.imageMediaId }),
        },
      },
      [{ entity: 'project', id }],
    );
    return id;
  }

  /** Board anlegen oder ändern (Name und Spalten). */
  async saveBoard(input: { id?: string; projectId: string; name: string; columns?: BoardColumn[]; archivedAt?: string | null }) {
    const ts = new Date().toISOString();
    const id = input.id ?? uuidv7();
    const prev = this.data.board.get(id);
    this.setLocal('board', {
      id,
      projectId: input.projectId,
      name: input.name,
      columns: input.columns ?? prev?.columns ?? [],
      archivedAt: input.archivedAt ?? null,
      createdAt: prev?.createdAt ?? ts,
      updatedAt: ts,
    });
    await this.queue(
      {
        opId: uuidv7(),
        entity: 'board',
        op: 'upsert',
        data: {
          id,
          projectId: input.projectId,
          name: input.name,
          archivedAt: input.archivedAt ?? null,
          ...(input.columns !== undefined && { columns: input.columns }),
        },
      },
      [{ entity: 'board', id }],
    );
    return id;
  }

  /** Board löschen; die Aufgaben bleiben und liegen danach auf keinem Board. */
  async deleteBoard(id: string) {
    const board = this.data.board.get(id);
    if (!board) return;
    const ts = new Date().toISOString();
    this.data.board.delete(id);
    const touched: { entity: EntityName; id: string }[] = [{ entity: 'board', id }];
    for (const t of this.data.task.values()) {
      if (t.boardId !== id) continue;
      this.setLocal('task', { ...t, boardId: null, columnId: null, updatedAt: ts });
      touched.push({ entity: 'task', id: t.id });
    }
    await this.queue({ opId: uuidv7(), entity: 'board', op: 'delete', data: { id, projectId: board.projectId, name: board.name } }, touched);
  }

  /** Aufgabe auf ein Board legen, verschieben (Spalte, Platz) oder herunternehmen. */
  async moveTask(taskId: string, to: { boardId: string | null; columnId?: string | null; boardOrder?: number }) {
    const prev = this.data.task.get(taskId);
    if (!prev) return;
    const ts = new Date().toISOString();
    const boardId = to.boardId;
    const columnId = boardId ? (to.columnId ?? null) : null;
    const boardOrder = to.boardOrder ?? prev.boardOrder;
    this.setLocal('task', { ...prev, boardId, columnId, boardOrder, updatedAt: ts });
    await this.queue(
      { opId: uuidv7(), entity: 'task', op: 'upsert', data: { id: taskId, projectId: prev.projectId, title: prev.title, boardId, columnId, boardOrder } },
      [{ entity: 'task', id: taskId }],
    );
  }

  async saveCategory(input: { id?: string; name: string; color?: string | null }) {
    const ts = new Date().toISOString();
    const id = input.id ?? uuidv7();
    const prev = this.data.category.get(id);
    this.setLocal('category', { id, name: input.name, color: input.color ?? null, createdAt: prev?.createdAt ?? ts, updatedAt: ts });
    await this.queue({ opId: uuidv7(), entity: 'category', op: 'upsert', data: { id, name: input.name, color: input.color ?? null } }, [{ entity: 'category', id }]);
    return id;
  }

  async saveTask(input: Partial<Pick<Task, 'priority' | 'dueAt' | 'assigneeIds' | 'categoryIds' | 'parentId' | 'subtaskOrder'>> & {
    id?: string;
    projectId: string;
    title: string;
    status?: 'offen' | 'erledigt';
  }) {
    const ts = new Date().toISOString();
    const id = input.id ?? uuidv7();
    const prev = this.data.task.get(id);
    const statusChange = input.status && prev && input.status !== prev.status && (input.status === 'erledigt' || prev.status === 'erledigt');
    this.setLocal('task', {
      id,
      projectId: input.projectId,
      title: input.title,
      status: statusChange ? input.status! : (prev?.status ?? 'offen'),
      step: prev?.step ?? 1,
      priority: input.priority ?? prev?.priority ?? 'normal',
      dueAt: input.dueAt !== undefined ? input.dueAt : (prev?.dueAt ?? null),
      assigneeIds: input.assigneeIds ?? prev?.assigneeIds ?? [],
      categoryIds: input.categoryIds ?? prev?.categoryIds ?? [],
      boardId: prev?.boardId ?? null,
      columnId: prev?.columnId ?? null,
      boardOrder: prev?.boardOrder ?? 0,
      createdBy: prev?.createdBy ?? this.me.userId,
      lastMessageAt: prev?.lastMessageAt ?? null,
      lastMessagePreview: prev?.lastMessagePreview ?? null,
      archivedAt: statusChange ? (input.status === 'erledigt' ? ts : null) : (prev?.archivedAt ?? null),
      // Ältere lokale Bestände kennen das Feld noch nicht
      parentId: input.parentId !== undefined ? input.parentId : (prev?.parentId ?? null),
      subtaskOrder: input.subtaskOrder ?? prev?.subtaskOrder ?? (input.parentId ? Math.max(0, ...subtasksOf(this, input.parentId).map(t => t.subtaskOrder ?? 0)) + 1000 : 0),
      createdAt: prev?.createdAt ?? ts,
      updatedAt: ts,
    });
    const { id: _ignored, ...rest } = input;
    await this.queue({ opId: uuidv7(), entity: 'task', op: 'upsert', data: { id, ...rest } }, [{ entity: 'task', id }]);
    return id;
  }

  async sendMessage(input: {
    taskId: string;
    kind: Exclude<Message['kind'], 'system'>;
    body?: string;
    stepRole?: Message['stepRole'];
    checkResult?: Message['checkResult'];
    replyToId?: string | null;
    replyPoint?: number | null;
    isMilestone?: boolean;
    questionTo?: string | null;
    form?: QuestionForm | null;
    /** Erwähnte Personen (Benutzer-IDs, `guest:<id>` für Externe) */
    mentions?: string[];
    /** Weiterleiten: vorhandenes Medium der Ursprungsnachricht, kein neuer Upload */
    mediaId?: string | null;
    forwarded?: boolean;
    upload?: Omit<PendingUpload, 'mediaId' | 'queuedAt'>;
  }) {
    const task = this.data.task.get(input.taskId);
    if (!task) throw new Error('Aufgabe nicht gefunden');
    const ts = new Date().toISOString();
    const id = uuidv7();
    const touched: { entity: EntityName; id: string }[] = [{ entity: 'message', id }, { entity: 'task', id: task.id }];

    let mediaId: string | null = input.mediaId ?? null;
    let media: Media | null = mediaId ? (this.data.media.get(mediaId) ?? null) : null;
    if (input.upload) {
      mediaId = uuidv7();
      const u = input.upload;
      media = {
        id: mediaId,
        kind: u.kind,
        mime: u.mime,
        fileName: u.fileName,
        size: null,
        durationSec: u.durationSec,
        width: u.width,
        height: u.height,
        status: 'hochladen',
        url: u.url,
        linkTitle: null,
        linkSite: u.url ? safeHost(u.url) : null,
        linkDescription: null,
        linkImage: false,
        summary: null,
        points: [],
        frames: [],
        transcript: null,
        imageText: null,
        aiError: null,
        aiAt: null,
        createdAt: ts,
        updatedAt: ts,
      };
      this.setLocal('media', media);
      touched.push({ entity: 'media', id: mediaId });
      const pending: PendingUpload = { ...u, mediaId, queuedAt: ts };
      this.uploads.push(pending);
      await this.storage.putUpload(pending);
    }

    const next = applyStepRole(task, input.stepRole ?? null, input.checkResult ?? null);
    const message: Message = {
      id,
      taskId: task.id,
      authorId: this.me.userId,
      authorName: this.me.userName,
      authorKind: 'user',
      kind: input.kind,
      body: input.body ?? '',
      stepRole: input.stepRole ?? null,
      checkResult: input.stepRole === 'pruefung' ? (input.checkResult ?? null) : null,
      stepNo: next.messageStep,
      replyToId: input.replyToId ?? null,
      replyPoint: input.replyPoint ?? null,
      mediaId,
      isMilestone: !!input.isMilestone || input.kind === 'meilenstein',
      questionTo: input.questionTo ?? null,
      questionOpen: input.kind === 'rueckfrage',
      form: input.kind === 'rueckfrage' ? (input.form ?? null) : null,
      mentions: input.mentions ?? [],
      editedAt: null,
      forwarded: !!input.forwarded,
      createdAt: ts,
      updatedAt: ts,
      deletedAt: null,
    };
    this.setLocal('message', message);
    this.setLocal('task', {
      ...task,
      status: next.status,
      step: next.step,
      archivedAt: next.status === 'erledigt' ? (task.archivedAt ?? ts) : null,
      lastMessageAt: ts,
      lastMessagePreview: `${this.me.userName}: ${messagePreview(input.kind, message.body, media)}`,
      updatedAt: ts,
    });

    await this.queue(
      {
        opId: uuidv7(),
        entity: 'message',
        op: 'upsert',
        data: {
          id,
          taskId: task.id,
          kind: input.kind,
          body: message.body,
          stepRole: message.stepRole,
          checkResult: message.checkResult,
          replyToId: message.replyToId,
          replyPoint: message.replyPoint,
          mediaId,
          isMilestone: message.isMilestone,
          questionTo: message.questionTo,
          ...(message.form && { form: message.form }),
          ...(message.mentions.length > 0 && { mentions: message.mentions }),
          ...(message.forwarded && { forwarded: true }),
          createdAt: ts,
        },
      },
      touched,
    );
    return id;
  }

  async updateMessage(id: string, patch: { isMilestone?: boolean; questionOpen?: boolean; body?: string; mentions?: string[] }) {
    const m = this.data.message.get(id);
    if (!m) return;
    const ts = new Date().toISOString();
    const edited = patch.body !== undefined && patch.body !== m.body;
    this.setLocal('message', { ...m, ...patch, ...(edited && { editedAt: ts }), updatedAt: ts });
    await this.queue(
      { opId: uuidv7(), entity: 'message', op: 'upsert', data: { id, taskId: m.taskId, kind: m.kind === 'system' ? 'text' : m.kind, ...patch } } as SyncOp,
      [{ entity: 'message', id }],
    );
  }

  /**
   * Eigene Nachricht löschen. Im Verlauf bleibt ein Hinweis stehen, Text und
   * Medium verschwinden. Hat die Nachricht den Server noch nicht erreicht,
   * werden ihre wartenden Operationen und der Upload gleich mit verworfen.
   */
  async deleteMessage(id: string) {
    const m = this.data.message.get(id);
    if (!m || m.deletedAt) return;
    const ts = new Date().toISOString();
    const unsent = this.outbox.filter((p) => p.op.entity === 'message' && (p.op.data as { id: string }).id === id);
    if (unsent.length) {
      this.outbox = this.outbox.filter((p) => !unsent.includes(p));
      await this.storage.removeOutbox(unsent.map((p) => p.op.opId));
    }
    if (m.mediaId && this.uploads.some((u) => u.mediaId === m.mediaId)) {
      this.uploads = this.uploads.filter((u) => u.mediaId !== m.mediaId);
      this.uploadErrors.delete(m.mediaId);
      await this.storage.removeUpload(m.mediaId);
    }
    this.setLocal('message', { ...m, body: '', mediaId: null, form: null, isMilestone: false, questionOpen: false, deletedAt: ts, updatedAt: ts });
    const touched: { entity: EntityName; id: string }[] = [{ entity: 'message', id }];
    const task = this.data.task.get(m.taskId);
    if (task && task.lastMessageAt === m.createdAt) {
      this.setLocal('task', { ...task, lastMessagePreview: `${m.authorName}: Nachricht gelöscht` });
      touched.push({ entity: 'task', id: task.id });
    }
    await this.queue(
      { opId: uuidv7(), entity: 'message', op: 'delete', data: { id, taskId: m.taskId, kind: m.kind === 'system' ? 'text' : m.kind } } as SyncOp,
      touched,
    );
  }

  /** Datei vormerken (z. B. für Antworten auf einen Fragebogen); liefert die Medien-ID. */
  async queueFile(u: Omit<PendingUpload, 'mediaId' | 'queuedAt' | 'url'>): Promise<string> {
    const ts = new Date().toISOString();
    const mediaId = uuidv7();
    this.setLocal('media', {
      id: mediaId,
      kind: u.kind,
      mime: u.mime,
      fileName: u.fileName,
      size: null,
      durationSec: u.durationSec,
      width: u.width,
      height: u.height,
      status: 'hochladen',
      url: null,
      linkTitle: null,
      linkSite: null,
      linkDescription: null,
      linkImage: false,
      summary: null,
      points: [],
      frames: [],
      transcript: null,
      imageText: null,
      aiError: null,
      aiAt: null,
      createdAt: ts,
      updatedAt: ts,
    });
    const pending: PendingUpload = { ...u, url: null, mediaId, queuedAt: ts };
    this.uploads.push(pending);
    await this.storage.putUpload(pending);
    await this.persist([{ entity: 'media', id: mediaId }]);
    return mediaId;
  }

  /** Eigene Antwort auf einen Fragebogen speichern (erneutes Absenden ändert sie). */
  async answerForm(messageId: string, answers: FormAnswers, voiceMediaId: string | null = null) {
    const m = this.data.message.get(messageId);
    if (!m?.form) throw new Error('Fragebogen nicht gefunden');
    const ts = new Date().toISOString();
    const key = `${messageId}:${this.me.userId}`;
    const prev = this.data.formResponse.get(key);
    const row: FormResponse = {
      id: key,
      messageId,
      taskId: m.taskId,
      userId: this.me.userId,
      userName: this.me.userName,
      authorKind: prev?.authorKind ?? 'user',
      answers,
      voiceMediaId: voiceMediaId ?? prev?.voiceMediaId ?? null,
      createdAt: prev?.createdAt ?? ts,
      updatedAt: ts,
    };
    this.setLocal('formResponse', row);
    await this.queue(
      {
        opId: uuidv7(),
        entity: 'formResponse',
        op: 'upsert',
        data: { messageId, taskId: m.taskId, answers, voiceMediaId: row.voiceMediaId, mediaIds: answerMediaIds(m.form, answers, row.voiceMediaId) },
      },
      [{ entity: 'formResponse', id: key }],
    );
  }

  /**
   * Nachricht in andere Aufgaben weiterleiten. Text und Medium werden
   * übernommen (das Medium nicht neu hochgeladen), der Vermerk
   * „Weitergeleitet“ steht dabei.
   */
  async forwardMessage(messageId: string, taskIds: string[]) {
    const m = this.data.message.get(messageId);
    if (!m || m.deletedAt) return;
    // Rückfragen und Meilensteine kommen als gewöhnliche Nachricht an
    const kind = m.kind === 'system' || m.kind === 'rueckfrage' || m.kind === 'meilenstein' ? 'text' : m.kind;
    for (const taskId of taskIds) {
      if (!this.data.task.get(taskId)) continue;
      await this.sendMessage({ taskId, kind, body: m.body, mediaId: m.mediaId, forwarded: true });
    }
  }

  /** Eigene Reaktion setzen; dasselbe Emoji noch einmal nimmt sie zurück. */
  async toggleReaction(messageId: string, emoji: string) {
    const m = this.data.message.get(messageId);
    if (!m) return;
    const id = `${messageId}:${this.me.userId}`;
    const prev = this.data.reaction.get(id);
    const ts = new Date().toISOString();
    if (prev?.emoji === emoji) {
      this.data.reaction.delete(id);
      await this.queue({ opId: uuidv7(), entity: 'reaction', op: 'delete', data: { messageId, taskId: m.taskId } } as SyncOp, [{ entity: 'reaction', id }]);
      return;
    }
    this.setLocal('reaction', {
      id,
      messageId,
      taskId: m.taskId,
      userId: this.me.userId,
      userName: this.me.userName,
      authorKind: 'user',
      emoji,
      createdAt: prev?.createdAt ?? ts,
      updatedAt: ts,
    });
    await this.queue({ opId: uuidv7(), entity: 'reaction', op: 'upsert', data: { messageId, taskId: m.taskId, emoji } }, [{ entity: 'reaction', id }]);
  }

  /** Punkt an die Checkliste einer Aufgabe anhängen. */
  async addChecklistItem(taskId: string, text: string) {
    const ts = new Date().toISOString();
    const id = uuidv7();
    const order = checklistOf(this, taskId).reduce((max, c) => Math.max(max, c.order), 0) + 1000;
    this.setLocal('checklistItem', {
      id,
      taskId,
      text: text.trim(),
      done: false,
      doneBy: null,
      doneByName: null,
      doneAt: null,
      order,
      createdBy: this.me.userId,
      createdAt: ts,
      updatedAt: ts,
      deletedAt: null,
    });
    await this.queue({ opId: uuidv7(), entity: 'checklistItem', op: 'upsert', data: { id, taskId, text: text.trim(), order } }, [{ entity: 'checklistItem', id }]);
    return id;
  }

  /** Text, Haken oder Reihenfolge eines Checklistenpunkts ändern. */
  async updateChecklistItem(id: string, patch: { text?: string; done?: boolean; order?: number }) {
    const prev = this.data.checklistItem?.get(id);
    if (!prev || prev.deletedAt) return;
    const ts = new Date().toISOString();
    const doneChange = patch.done !== undefined && patch.done !== prev.done;
    this.setLocal('checklistItem', {
      ...prev,
      text: patch.text?.trim() || prev.text,
      done: patch.done ?? prev.done,
      doneBy: doneChange ? (patch.done ? this.me.userId : null) : prev.doneBy,
      doneByName: doneChange ? (patch.done ? this.me.userName : null) : prev.doneByName,
      doneAt: doneChange ? (patch.done ? ts : null) : prev.doneAt,
      order: patch.order ?? prev.order,
      updatedAt: ts,
    });
    await this.queue({ opId: uuidv7(), entity: 'checklistItem', op: 'upsert', data: { id, taskId: prev.taskId, ...patch } }, [{ entity: 'checklistItem', id }]);
  }

  async deleteChecklistItem(id: string) {
    const prev = this.data.checklistItem?.get(id);
    if (!prev || prev.deletedAt) return;
    const ts = new Date().toISOString();
    this.setLocal('checklistItem', { ...prev, deletedAt: ts, updatedAt: ts });
    await this.queue({ opId: uuidv7(), entity: 'checklistItem', op: 'delete', data: { id, taskId: prev.taskId } } as SyncOp, [{ entity: 'checklistItem', id }]);
  }

  /** Nachricht für sich markieren bzw. die Markierung entfernen. */
  async toggleStar(messageId: string) {
    const m = this.data.message.get(messageId);
    if (!m) return;
    const cur = this.data.taskUserState.get(`${m.taskId}:${this.me.userId}`)?.starred ?? [];
    const starred = cur.includes(messageId) ? cur.filter((x) => x !== messageId) : [...cur, messageId];
    await this.setTaskUserState(m.taskId, { starred });
  }

  async setTaskUserState(taskId: string, patch: Partial<Pick<TaskUserState, 'pinned' | 'muted' | 'lastReadAt' | 'starred'>>) {
    const key = `${taskId}:${this.me.userId}`;
    const prev = this.data.taskUserState.get(key);
    if (patch.lastReadAt && prev?.lastReadAt && patch.lastReadAt <= prev.lastReadAt && Object.keys(patch).length === 1) return;
    this.setLocal('taskUserState', {
      id: key,
      taskId,
      userId: this.me.userId,
      pinned: patch.pinned ?? prev?.pinned ?? false,
      muted: patch.muted ?? prev?.muted ?? false,
      lastReadAt: patch.lastReadAt ?? prev?.lastReadAt ?? null,
      starred: patch.starred ?? prev?.starred ?? [],
      updatedAt: new Date().toISOString(),
    });
    await this.queue({ opId: uuidv7(), entity: 'taskUserState', op: 'upsert', data: { taskId, ...patch } }, [{ entity: 'taskUserState', id: key }]);
  }

  // ---------------------------------------------------------------------------
  // Synchronisation

  /** Führt Upload → Push → Pull aus. Parallele Aufrufe werden zusammengefasst. */
  flush(): Promise<void> {
    if (this.flushing) {
      this.flushAgain = true;
      return this.flushing;
    }
    this.flushing = (async () => {
      do {
        this.flushAgain = false;
        await this.runOnce();
      } while (this.flushAgain);
    })().finally(() => {
      this.flushing = null;
    });
    return this.flushing;
  }

  private async runOnce() {
    if (!this.status.online) return;
    this.status = { ...this.status, syncing: true };
    this.emit();
    try {
      // Ein fehlgeschlagener Upload blockiert nur seine eigene Nachricht,
      // alles andere wird trotzdem abgeglichen.
      const uploadProblem = await this.uploadPending();
      await this.pushPending();
      await this.pullAll();
      const now = new Date().toISOString();
      this.status = { ...this.status, lastError: uploadProblem, lastSyncAt: now };
      await this.storage.setMeta('lastSyncAt', now);
    } catch (err) {
      this.status = { ...this.status, lastError: err instanceof Error ? err.message : String(err) };
    } finally {
      this.status = { ...this.status, syncing: false };
      this.emit();
    }
  }

  /** Lädt ausstehende Medien hoch. Gibt eine Fehlermeldung zurück, wenn mindestens eins scheiterte. */
  private async uploadPending(): Promise<string | null> {
    let problem: string | null = null;
    for (const u of [...this.uploads]) {
      try {
        await this.transport.upload(u);
        this.uploads = this.uploads.filter((x) => x.mediaId !== u.mediaId);
        this.uploadErrors.delete(u.mediaId);
        await this.storage.removeUpload(u.mediaId);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        // Ohne Verbindung bringen weitere Versuche jetzt nichts
        if ((err as { status?: number }).status === 0) throw err;
        this.uploadErrors.set(u.mediaId, message);
        problem = `Nicht gesendet: ${message}`;
      }
    }
    return problem;
  }

  private async pushPending() {
    while (this.outbox.length > 0) {
      // Operationen mit noch nicht hochgeladenem Medium zurückhalten
      const waiting = new Set(this.uploads.map((u) => u.mediaId));
      const refersToPending = (p: PendingOp) => {
        const d = p.op.data as { mediaId?: string | null; imageMediaId?: string | null; mediaIds?: string[] };
        return (!!d.mediaId && waiting.has(d.mediaId)) || (!!d.imageMediaId && waiting.has(d.imageMediaId)) || !!d.mediaIds?.some((m) => waiting.has(m));
      };
      const batch = this.outbox.filter((p) => !refersToPending(p)).slice(0, 200);
      if (batch.length === 0) return;
      const res = await this.transport.push({ deviceId: this.deviceId, ops: batch.map((b) => b.op) });
      const done = res.results.map((r) => r.opId);
      for (const r of res.results) if (!r.ok) this.rejected.push({ opId: r.opId, error: r.error ?? 'Abgelehnt' });
      this.outbox = this.outbox.filter((p) => !done.includes(p.op.opId));
      await this.storage.removeOutbox(done);
      if (done.length === 0) return;
    }
  }

  private async pullAll() {
    for (let i = 0; i < 50; i++) {
      const res = await this.transport.pull(this.cursor);
      const rows = res.changes.map((c) => ({ entity: c.entity, id: c.id, data: c.data }));
      for (const r of rows) {
        // Lokal noch ausstehende Änderungen nicht überschreiben
        if (this.isPending(r.entity, r.id)) continue;
        const map = this.data[r.entity] as Map<string, any>;
        if (r.data) map.set(r.id, r.data);
        else map.delete(r.id);
      }
      await this.storage.putRows(rows.filter((r) => !this.isPending(r.entity, r.id)));
      this.cursor = res.cursor;
      await this.storage.setMeta('cursor', String(this.cursor));
      if (rows.length) this.emit();
      if (!res.hasMore) return;
    }
  }

  /** Vom Server gemeldeter neuer Stand (SSE). */
  notifyRemoteCursor(cursor: number) {
    if (cursor > this.cursor) void this.flush();
  }
}

/** ID der Entität, die eine Operation betrifft. */
function opKey(op: SyncOp, userId: string): string | undefined {
  const d = op.data as { id?: string; messageId?: string; taskId?: string };
  if (op.entity === 'formResponse' || op.entity === 'reaction') return `${d.messageId}:${userId}`;
  if (op.entity === 'taskUserState') return `${d.taskId}:${userId}`;
  return d.id;
}

function safeHost(url: string): string | null {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

// -----------------------------------------------------------------------------
// Abfragen für die Oberflächen

/** Ungelesene Nachrichten je Aufgabe in einem Durchlauf. */
export function unreadByTask(client: SyncClient, userId: string): Map<string, number> {
  const counts = new Map<string, number>();
  for (const m of client.data.message.values()) {
    if (m.authorId === userId || m.deletedAt || m.authorKind === 'system') continue;
    const since = client.data.taskUserState.get(`${m.taskId}:${userId}`)?.lastReadAt ?? '';
    if (m.createdAt > since) counts.set(m.taskId, (counts.get(m.taskId) ?? 0) + 1);
  }
  return counts;
}

/** Antworten auf einen Fragebogen, älteste zuerst. */
export function responsesOf(client: SyncClient, messageId: string): FormResponse[] {
  return [...client.data.formResponse.values()]
    .filter((r) => r.messageId === messageId)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export function messagesOfTask(client: SyncClient, taskId: string): Message[] {
  return [...client.data.message.values()]
    .filter((m) => m.taskId === taskId)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id));
}

/** Checkliste einer Aufgabe in ihrer Reihenfolge, ohne gelöschte Punkte. */
export function checklistOf(client: SyncClient, taskId: string): ChecklistItem[] {
  return [...(client.data.checklistItem?.values() ?? [])]
    .filter((c) => c.taskId === taskId && !c.deletedAt)
    .sort((a, b) => a.order - b.order || a.createdAt.localeCompare(b.createdAt));
}

/** Unteraufgaben einer Hauptaufgabe: offene zuerst, dann in der Reihenfolge ihres Anlegens. */
export function subtasksOf(client: SyncClient, taskId: string): Task[] {
  return [...client.data.task.values()]
    .filter((t) => (t.parentId ?? null) === taskId)
    .sort((a, b) => (a.subtaskOrder ?? 0) - (b.subtaskOrder ?? 0) || a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id));
}

/** Fortschritt einer Aufgabe: erledigte Unteraufgaben und abgehakte Checklistenpunkte. */
export function taskProgress(client: SyncClient, taskId: string): { subtasks: { done: number; total: number }; checklist: { done: number; total: number } } {
  const subs = subtasksOf(client, taskId);
  const items = checklistOf(client, taskId);
  return {
    subtasks: { done: subs.filter((t) => t.status === 'erledigt').length, total: subs.length },
    checklist: { done: items.filter((c) => c.done).length, total: items.length },
  };
}

/** Reaktionen einer Nachricht, gruppiert nach Emoji (häufigste zuerst). */
export function reactionsOf(client: SyncClient, messageId: string, userId: string): { emoji: string; count: number; names: string[]; mine: boolean }[] {
  const groups = new Map<string, { emoji: string; count: number; names: string[]; mine: boolean; first: string }>();
  for (const r of client.data.reaction?.values() ?? []) {
    if (r.messageId !== messageId) continue;
    const g = groups.get(r.emoji) ?? { emoji: r.emoji, count: 0, names: [], mine: false, first: r.createdAt };
    g.count++;
    g.names.push(r.userName);
    if (r.userId === userId) g.mine = true;
    if (r.createdAt < g.first) g.first = r.createdAt;
    groups.set(r.emoji, g);
  }
  return [...groups.values()].sort((a, b) => b.count - a.count || a.first.localeCompare(b.first)).map(({ first: _f, ...g }) => g);
}
